import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/CustomersService';
import {Customer} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';
import {customersKeys} from './useCustomersQuery';

/**
 * FNP-918 (§6): build the mutation payload by WHITELISTING the fields the API's
 * Input type declares.
 *
 * It was previously built by spreading the record and `delete`-ing `created`.
 * Two problems with that shape:
 *
 *   1. It MUTATES nothing here only because the spread copies first - but the
 *      same pattern one layer up (`delete record.created` on the caller's
 *      object) mutates the live Formik/React Query object. `applications`
 *      shipped exactly that for months (FNP-996).
 *   2. It is a DENYLIST. Every field the UI adds to the record - the derived
 *      `createdAtMillis` added by useCustomers, for one - is silently sent to
 *      the API until someone remembers to `delete` it too. A whitelist cannot
 *      leak a field nobody thought about.
 *
 * `createdAtMillis` is the concrete case: it is a client-side derivation and
 * would be rejected by the API's Input type.
 */
const toCustomerInput = (values: Customer) => ({
    name: values.name,
    description: values.description,
    email: values.email,
    phone: values.phone,
});

export const useCustomer = (customerId?: string) => {
    const queryClient = useQueryClient();

    const customerQuery = useQuery({
        queryKey: ['customer', customerId],
        queryFn: () => fetch(customerId!),
        enabled: !!customerId,
    });

    const createMutation = useMutation({
        mutationFn: (values: Customer) => add(toCustomerInput(values)),
        onSuccess: () => {
            notifySuccess('Customer created');
            queryClient.invalidateQueries({queryKey: customersKeys.all});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: Customer) => {
            if (!customerId) throw new Error('customerId is required parameter');
            return update(customerId, {
                id: customerId,
                ...toCustomerInput(values),
            } as Customer);
        },
        onSuccess: () => {
            notifySuccess('Customer updated');
            // Detail-page mutations invalidate BOTH the detail key and the
            // list key, or the list keeps showing the pre-edit name (§8).
            queryClient.invalidateQueries({queryKey: customersKeys.all});
            queryClient.invalidateQueries({queryKey: ['customer', customerId]});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!customerId) throw new Error('customerId is required parameter');
            return deleteRecord(customerId);
        },
        onSuccess: () => {
            notifySuccess('Customer deleted');
            queryClient.invalidateQueries({queryKey: customersKeys.all});
        },
        onError: notifyError,
    });

    return {
        record: customerQuery.data,
        loading: customerQuery.isLoading,
        fetchRecord: customerQuery.refetch,
        // ⚠️ mutateAsync, NOT mutate (§6/§7).
        //
        // `mutate` is fire-and-forget: it returns void, so the drawer's
        // `await handleSubmit(values); onClose();` awaits `undefined` and
        // closes IMMEDIATELY. A server-side rejection then destroys the
        // operator's input and toasts into a closed drawer, and Formik clears
        // `isSubmitting` on the same tick so the submit button's `disabled`
        // never engages. `mutateAsync` returns the real promise, which makes
        // both the drawer's await and the disabled-while-submitting wiring do
        // what they already claim to do.
        createRecord: createMutation.mutateAsync,
        updateRecord: updateMutation.mutateAsync,
        deleteRecord: deleteMutation.mutateAsync,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
